/**
 * @module experience/landing
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
// Core Libraries
/* tslint:disable:no-any */ // Need to support all data types
import { EventEmitter, Injectable } from '@angular/core';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { CONTENT_ENDPOINT } from '@core/aem/content-endpoints';
// Services
import { ApiGatewayService } from '@core/public';
import { Wove } from 'aspect.js/';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { UserService } from '@support/shared/services/user.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
// Interfaces
import { GetNotifications } from '@support/api/proxies/responses/api-342.interface';
import { CustomerAccountLineDeviceDetails } from '@support/api/proxies/responses/api-2022.interface';
import { OrderSearchResponse } from '@support/api/proxies/responses/api-2813.interface';
import { TotalPendingOrdersResponse } from '@support/api/proxies/responses/api-2817.interface';
import { CustomerAccountBillingDetails } from '@support/api/proxies/responses/api-2006.interface';
import {
  FaqContent,
  FaqItem,
  ContentItem,
  ArrayData,
} from '@experience/landing/common/components/account-landing/interfaces/account-landing.interface';
import { ConfigData, AuthorableData } from '@experience/repdash/interfaces/aem-content.interface';
import { KeyValueMap } from '@support/shared/interfaces/key-value-map.interface';
// Constants
import { TEXT_CONSTANTS } from '@support/shared/constants/device.constants';
import { ACCOUNT_LANDING_MODULE_COMPONENTS } from '@experience/landing/common/constants/account-landing.constants';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import { BILLING_CONSTANTS } from '@experience/billing-landing/components/billing-landing.constants';
import { throwError as observableThrowError, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpResponse, HttpHeaders } from '@angular/common/http';

@Wove({ moduleName: ACCOUNT_LANDING_MODULE_COMPONENTS.ACCOUNT_LANDING_MODULE_NAME })
/**
 * User service function related to User for account landing component
 */
@Injectable()
export class AccountLandingService {
  public faqContentData: FaqContent;
  public items: FaqItem;
  public keyArray: ContentItem[];
  public keyContent: ArrayData;
  public ctaContent: ArrayData;
  public billingConstants: KeyValueMap;
  /**
   * Holds modal heading
   */
  public modalHeading: string;
  /**
   * Holds modal text
   */
  public modalText: string;
  /**
   * Holds boolean if account is cancelled
   */
  public isCancelledAccount: boolean;
  /**
   * Holds msisdn status
   */
  public msisdnStatus: string;
  /**
   * Holds boolean account is closed
   */
  public isAccountClosed: boolean;
  /**
   * Hold open Modal flag
   */
  public openModal: boolean;
  /**
   *  Holds route Id
   */
  public routeId: string;
  /**
   * To set device page on support landing
   */
  public isDevice: boolean;
  /*
   * To maintain a check for navigation for the first time only
   */
  public checkNavigation: boolean;
  constructor(
    private apiGatewayService: ApiGatewayService,
    private contentProviderService: ContentProviderService,
    private commonUtils: CommonUtilService,
    private userService: UserService,
    private stateStoreUtilsService: StateStoreUtilsService
  ) {
    this.isDevice = false;
    this.checkNavigation = true;
    this.billingConstants = BILLING_CONSTANTS;
  }
  /**
   * Gets the notification
   * @param customerid : stores the id of the customer
   * @returns GetNotifications
   */
  public getNotifications(customerId: string): Observable<GetNotifications> {
    const headers = new HttpHeaders();
    return this.apiGatewayService
      .getRequest(API_MAP.API_342_getNotifications, headers, null, { customerId: customerId })
      .pipe(
        map((response: HttpResponse<GetNotifications>) => {
          return response.body;
        })
      );
  }

  /**
   * Calls content-provider-service to fetch AEM data.
   * @param pageUrl String dynamic part of AEM call.
   * @returns object
   */
  public getContentData(pageUrl?: string): Observable<object> {
    return this.contentProviderService.getContentData(pageUrl).pipe(
      map((contentData: object) => {
        if (pageUrl === CONTENT_ENDPOINT.SUPPORT_LANDING) {
          this.getSupportContent(contentData);
        }
        return this.contentProviderService.getConfigurableData(contentData);
      })
    );
  }

  public getSupportContent(supportContent: object): void {
    this.faqContentData = supportContent[1];
    this.items = this.faqContentData.data.items[0].data;
    this.keyArray = this.items.items;
    this.keyArray.forEach((keyObject: ContentItem, key: number) => {
      if (this.keyArray[key].uiComponent === 'bulletList') {
        this.keyContent = this.keyArray[key].data;
      } else if (this.keyArray[key].uiComponent === 'cta-link') {
        this.ctaContent = this.keyArray[key].data;
      }
    });
  }
  /**
   * Gets alert for text length
   * @returns number
   */
  public getAlertTextLengthLimit(): number {
    const device: string = this.commonUtils.checkDevice();
    let textLength: number;
    switch (true) {
      case device === TEXT_CONSTANTS.DEVICE_DESKTOP:
        textLength = TEXT_CONSTANTS.DESKTOP_CHAR_LIMIT;
        break;
      case device === TEXT_CONSTANTS.DEVICE_TABLET:
        textLength = TEXT_CONSTANTS.TABLET_CHAR_LIMIT;
        break;
      case device === TEXT_CONSTANTS.DEVICE_MOBILE:
        textLength = TEXT_CONSTANTS.MOBILE_CHAR_LIMIT;
        break;
      default:
        textLength = TEXT_CONSTANTS.DESKTOP_CHAR_LIMIT;
    }
    return textLength;
  }

  /**
   * Method for getting content data for line details
   * @returns object
   */
  public getLineDetailsContentData(): Observable<object> {
    return this.contentProviderService.getContentData().pipe(
      map((contentData: ConfigData) => {
        const configurableData = <ConfigData>(
          this.contentProviderService.getConfigurableData(contentData)
        );
        const authorableData = <AuthorableData>(
          this.contentProviderService.getAuthorableData(contentData)
        );
        return {
          configurableData: configurableData,
          authorableData: authorableData,
        };
      }),
      catchError((error: object) => {
        return observableThrowError(error);
      })
    );
  }

  /**
   * Method to return account line device details from API 2022
   * @param {string} msisdn
   *  @returns {Observable<CustomerAccountLineDeviceDetails>}
   * @memberof AccountLandingService
   */
  public getCustomerLineDeviceDetails(
    msisdn: string
  ): Observable<CustomerAccountLineDeviceDetails> {
    return this.userService.getCustomerAccountLineDeviceDetails(msisdn);
  }

  /**
   * Method for calling API 2813 to get line pending orders
   * @param lookUpParams : parameters to be passed in the get request
   * @returns {Observable<OrderSearchResponse>}
   */
  public getPendingOrderDetails(lookUpParams: {
    [name: string]: string;
  }): Observable<OrderSearchResponse> {
    return this.userService.getPendingOrderResults(lookUpParams);
  }

  /**
   * method for calling API 2817 to get pending orders
   * @returns {Observable<GetTotalPendingOrders>}
   * @memberof AccountLandingService
   */
  public getPendingOrderCount(): Observable<TotalPendingOrdersResponse> {
    return this.userService.getTotalPendingOrderCount();
  }

  /**
   * Creates fraud Model
   * @returns StaticModal
   */
  public createFraudModal(): StaticModal {
    return {
      headerText: '',
      bodyText: '',
      disableDismiss: false,
      disableEsc: true,
      dismissableMask: false,
      buttons: [
        {
          id: 'ok',
          type: 'submit',
          name: '',
          class: 'btn btn-primary',
        },
      ],
      toggleModalEmitter: new EventEmitter<Object>(),
    };
  }

  /**
   * open payment disclaimer modal
   * @param value, textValue
   * @returns void
   */
  public openDisclaimerModal(
    textValue: string,
    customerAccountBillingDetails: CustomerAccountBillingDetails,
    contentData: ConfigData
  ): boolean {
    this.isAccountClosed = false;
    this.isCancelledAccount = false;
    this.msisdnStatus = '';
    this.openModal = true;
    this.checkMsisdnStatus(customerAccountBillingDetails);
    if (customerAccountBillingDetails.accountStatus === this.billingConstants.CANCELLED) {
      this.modalHeading = contentData.paymentNotAllowed;
      this.modalText = contentData.paymentDisclaimerCancelledAccount;
      this.isCancelledAccount = true;
    } else if (customerAccountBillingDetails.accountStatus === this.billingConstants.CLOSED) {
      this.modalHeading = contentData.paymentNotAllowed;
      this.modalText = contentData.paymentDisclaimerClosedAccount;
      this.isAccountClosed = true;
    } else {
      if (this.msisdnStatus === this.billingConstants.CANCELLED) {
        (this.modalHeading = contentData.warning),
          (this.modalText = contentData.paymentDisclaimerCancelledMsisdn);
        this.routeId = textValue;
      } else {
        this.openModal = false;
      }
    }
    return this.openModal;
  }

  /**
   * to check the selected msisdn status
   * @returns string | msisdnStatus
   */
  public checkMsisdnStatus(customerAccountBillingDetails: CustomerAccountBillingDetails): void {
    const selectedMsisdn = this.stateStoreUtilsService.getMsisdn();
    for (let i = 0; i < customerAccountBillingDetails.lines.length; i++) {
      if (
        selectedMsisdn === customerAccountBillingDetails.lines[i].msisdn &&
        customerAccountBillingDetails.lines[i].lineStatus === this.billingConstants.CANCELLED
      ) {
        this.msisdnStatus = customerAccountBillingDetails.lines[i].lineStatus;
        this.stateStoreUtilsService.setSimNumber(
          customerAccountBillingDetails.lines[i].networkResource.simNumber
        );
      }
    }
  }
}
